if(!customElements.get('wlsn-ingredients')) {
  class WLSNIngredients extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        slider: 'js-wlsn-slider',
        pagination: 'js-wlsn-slider-pagination',
        navigation: 'js-slider-navigation',
        navigationHidden: 'hidden',
        nextButton: 'js-wlsn-slider-next',
        prevButton: 'js-wlsn-slider-prev',
        ingredientTab: 'js-wlsn-ingredient-tab',
        ingredientMedia: 'js-wlsn-ingredient-media',
        ingredientTabMedia: 'js-wlsn-ingredients-tab-media'
      }

      this.swiperInstance = null;
      this.onTabClick = null;
      this.autoplayInterval = null;
      this.activeIndex = 0;
    }

    get elements() {
      return {
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButtons: this.querySelectorAll(`.${this.classes.nextButton}`),
        prevButtons: this.querySelectorAll(`.${this.classes.prevButton}`),
        paginations: this.querySelectorAll(`.${this.classes.pagination}`),
        navigations: this.querySelectorAll(`.${this.classes.navigation}`),
        ingredientTabs: this.querySelectorAll(`.${this.classes.ingredientTab}`),
        ingredientTabMedia: this.querySelector(`.${this.classes.ingredientTabMedia}`),
      }
    }

    connectedCallback() {
      this._initSlider();
    }

    disconnectedCallback() {
      this._unbindTabEvents();
      this._stopAutoplay();

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    _initSlider() {
      const { slider } = this.elements;
      if (!slider) {
        this._hideNavigation();
        return;
      }

      this.swiperInstance = new Swiper(slider, {
        slidesPerView: 1.35,
        spaceBetween: 12,
        centeredSlides: true,
        loop: true,
        navigation: {
          nextEl: [...this.elements.nextButtons],
          prevEl: [...this.elements.prevButtons]
        },
        pagination: {
          el: [...this.elements.paginations],
          clickable: true
        },
        on: {
          init: () => {
            this.activeIndex = this.swiperInstance ? this.swiperInstance.activeIndex : 0;
            this._syncActiveTabState();
            this._updateNavigationVisibility();
          },
          slideChange: () => {
            this.activeIndex = this.swiperInstance ? this.swiperInstance.activeIndex : this.activeIndex;
            this._stopAutoplay();
            this._startAutoplay();
            this._syncActiveTabState();
            const wrapper = this.querySelector('[data-wlsn-slider-animate]');
            if (!wrapper) return;
            wrapper.querySelectorAll('.swiper-slide[data-animation-trigger]').forEach((slide) => {
              slide.removeAttribute('data-animation-trigger');
              slide.removeAttribute('data-animation-desktop');
              slide.removeAttribute('data-animation-mobile');
              slide.removeAttribute('data-delay');
            });
          },
          breakpoint: () => {
            this._updateNavigationVisibility();
          },
        },
        breakpoints: {
          768: {
            slidesPerView: 2.3,
            spaceBetween: 16,
            centeredSlides: false,
            loop: false,
          },
          1024: {
            slidesPerView: 6,
            spaceBetween: 16,
            centeredSlides: false,
            loop: false,
          },
        },
      });

      this._bindTabEvents();
      this._syncActiveTabState();
      this._startAutoplay();
      this._updateNavigationVisibility();
    }

    _updateNavigationVisibility() {
      if (!this.swiperInstance) {
        this._hideNavigation();
        return;
      }
      const slideCount = this.elements.ingredientTabs.length;
      const slidesPerView = parseFloat(this.swiperInstance.params.slidesPerView) || 1;
      const minSlidesForNav = Math.ceil(slidesPerView);
      if (slideCount > minSlidesForNav) {
        this._showNavigation();
      } else {
        this._hideNavigation();
      }
    }

    _showNavigation() {
      this.elements.navigations.forEach((el) => el.classList.remove(this.classes.navigationHidden));
    }

    _hideNavigation() {
      this.elements.navigations.forEach((el) => el.classList.add(this.classes.navigationHidden));
    }

    _bindTabEvents() {
      this._unbindTabEvents();

      this.onTabClick = (event) => {
        const tabs = [...this.elements.ingredientTabs];
        const clickedIndex = tabs.indexOf(event.currentTarget);
        if (clickedIndex === -1) return;
        this._goToIndex(clickedIndex);
        this._syncActiveTabState();
        this._stopAutoplay();
        this._startAutoplay();
      };

      this.elements.ingredientTabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });
    }

    _unbindTabEvents() {
      if (!this.onTabClick) return;

      this.elements.ingredientTabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
      });

      this.onTabClick = null;
    }

    _syncActiveTabState() {
      const { ingredientTabs } = this.elements;
      if (!ingredientTabs.length) return;

      const maxIndex = ingredientTabs.length - 1;
      const activeIndex = Math.min(this.activeIndex, maxIndex);
      const slides = this.swiperInstance ? [...this.swiperInstance.slides] : [];

      ingredientTabs.forEach((tab, index) => {
        tab.classList.toggle('active', index === activeIndex);
      });

      slides.forEach((slide, index) => {
        slide.classList.toggle('active', index === activeIndex);
      });

      this._renderActiveTabMedia(ingredientTabs[activeIndex]);
    }

    _goToIndex(index) {
      const { ingredientTabs } = this.elements;
      if (!ingredientTabs.length) return;

      const normalizedIndex = ((index % ingredientTabs.length) + ingredientTabs.length) % ingredientTabs.length;
      this.activeIndex = normalizedIndex;

      const canSlide = this.swiperInstance && !this.swiperInstance.isLocked;
      if (canSlide) {
        this.swiperInstance.slideTo(normalizedIndex);
        return;
      }

      this._syncActiveTabState();
    }

    _startAutoplay() {
      this._stopAutoplay();

      const tabsCount = this.elements.ingredientTabs.length;
      if (tabsCount <= 1) return;

      this.autoplayInterval = window.setInterval(() => {
        this._goToIndex(this.activeIndex + 1);
        this._syncActiveTabState();
      }, 5000);
    }

    _stopAutoplay() {
      if (!this.autoplayInterval) return;
      window.clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    _renderActiveTabMedia(activeTab) {
      const { ingredientTabMedia } = this.elements;
      if (!ingredientTabMedia) return;

      ingredientTabMedia.innerHTML = '';
      if (!activeTab) return;

      const media = activeTab.querySelector(`.${this.classes.ingredientMedia}`);
      if (!media) return;

      const mediaClone = media.cloneNode(true);
      ingredientTabMedia.appendChild(mediaClone);

      const mediaVideo = ingredientTabMedia.querySelector('video');
      if (!mediaVideo) return;

      mediaVideo.autoplay = true;
      mediaVideo.muted = true;
      mediaVideo.loop = true;
      mediaVideo.playsInline = true;
      mediaVideo.setAttribute('muted', 'muted');
      mediaVideo.setAttribute('playsinline', 'playsinline');

      const playPromise = mediaVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }

  customElements.define('wlsn-ingredients', WLSNIngredients);
}